import React from 'react';
import {
  Button,
  TextField,
  TextStyle,
  Link,
  Banner
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { Column, Row, getTranslatedText } from '@nile/components';
import { PSA_CONSTANTS } from '@nile/components/src/Constants/PSA';
import nanoid from 'nanoid';
import { getDefaultTemplate } from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceSettingsTemplate';
import { SuperSelect } from '../SuperSelect';

function ReminderList({
  reminders = [],
  onChange,
  onViewTemplate,
  isDisabled
}) {
  const intl = useIntl();
  const options = [
    {
      label: getTranslatedText(intl, 'common.before'),
      value: PSA_CONSTANTS.BEFORE
    },
    { label: getTranslatedText(intl, 'common.on'), value: PSA_CONSTANTS.ON },
    {
      label: getTranslatedText(intl, 'common.after'),
      value: PSA_CONSTANTS.AFTER
    }
  ];

  const updateRow = (reminderId, patch) => {
    const rows = reminders.map((reminder) => {
      if (reminder?.reminderId !== reminderId) return reminder;
      const updated = {
        ...reminder,
        reminderConfig: {
          ...(reminder?.reminderConfig || {}),
          ...patch
        }
      };
      if (updated?.reminderConfig?.triggerType === 'ON') {
        updated.reminderConfig.offsetValue = null;
      }
      return updated;
    });
    if (onChange) {
      onChange(rows);
    }
  };

  const addRow = () => {
    const rows = [
      ...reminders,
      {
        reminderConfig: { triggerType: null, offsetValue: null },
        reminderId: nanoid(),
        reminderTemplate: getDefaultTemplate()
      }
    ];
    if (onChange) onChange(rows);
  };

  const removeRow = (reminderId) => {
    const rows = reminders.filter((r) => r?.reminderId !== reminderId);
    if (onChange) onChange(rows);
  };

  return (
    <Column gap={15} className={isDisabled ? 'disable-actions' : ''}>
      {reminders.map((row, index) => {
        const needsDays =
          row?.reminderConfig?.triggerType === 'BEFORE' ||
          row?.reminderConfig?.triggerType === 'AFTER';
        return (
          <Row key={row.reminderId} className="items-center" gap={10}>
            <TextStyle variation="subdued" weight="SemiBold">
              <FormattedMessage
                id="invoice.reminder.count"
                values={{ count: index + 1 }}
              />
            </TextStyle>
            <SuperSelect
              width={140}
              value={options.find(
                (opt) => opt.value === row?.reminderConfig?.triggerType
              )}
              onChange={(opt) =>
                updateRow(row?.reminderId, { triggerType: opt?.value || null })
              }
              options={options}
              error={row?.error?.triggerType?.[0]}
            />
            {needsDays && (
              <div className="fx actr g8">
                <TextField
                  suffix="Days"
                  value={String(row?.reminderConfig?.offsetValue)}
                  onChange={(text) => {
                    const num = text === '' ? null : Number(text);
                    let offsetValue = null;
                    if (!Number.isNaN(num)) {
                      offsetValue = num > 60 ? 60 : num;
                    }
                    updateRow(row?.reminderId, { offsetValue });
                  }}
                  type="number"
                  autoComplete="off"
                  error={row?.error?.offsetValue?.[0]}
                />
              </div>
            )}
            <TextStyle variation="subdued" weight="SemiBold">
              <FormattedMessage id="invoice.reminder.dueDate" />
            </TextStyle>
            <div className="fx actr jend g8">
              <Link
                onClick={() =>
                  onViewTemplate && onViewTemplate(row?.reminderId)
                }
              >
                <FormattedMessage id="invoice.reminder.viewTemplate" />
              </Link>
              <div
                onClick={() => removeRow(row?.reminderId)}
                className="cursor-pointer fx"
              >
                <SVGIcon name="Close" size="sm" />
              </div>
            </div>
          </Row>
        );
      })}

      {reminders.length < 3 && (
        <div className="mt-5">
          <Button plain icon={<SVGIcon name="Add" />} onClick={addRow}>
            <FormattedMessage id="invoice.reminder.add" />
          </Button>
        </div>
      )}

      {reminders.length === 3 && (
        <Banner status="info">
          <FormattedMessage id="invoice.reminder.excessBanner" />
        </Banner>
      )}
    </Column>
  );
}

export default ReminderList;
